import * as THREE from 'three';
import { CHARGE_DAMAGE, CHARGE_KNOCKBACK, MELEE_DAMAGE, RATKING_RADIUS, type RatKingBoss } from '../ai/ratking';
import type { Projectile, ProjectilePool } from './projectiles';
import { applyDamage, type Health } from './damage';
import { withinEngagementRange } from './spatial';
import type { MeleeSwing } from './weapon';
import type { DamageEvent } from './resolve';

/**
 * Rat King parallels to `combat/bossResolve.ts`'s Gridlock functions — a
 * SEPARATE file, not an extension of that one, matching `ai/ratking.ts`'s
 * own reasoning for why `RatKingBoss` is a sibling to `Boss` rather than a
 * shared config variant (see that file's header comment). Not wired into
 * `main.ts` this session (ai/ratking.ts isn't live-spawned yet) — these
 * exist ready for whoever wires the real den encounter (Wave D/M
 * coordinated follow-up per PLAN-CITY2 §10).
 */

const PLAYER_HIT_INVULN_MS = 250; // matches combat/resolve.ts's and combat/bossResolve.ts's own constant — same post-hit grace window regardless of what hit the player
const MELEE_KNOCKBACK = 4; // a bite/slam, not a launch — noticeably lighter than the charge's own CHARGE_KNOCKBACK

/** Player bullets/pellets vs. the Rat King. Same ordering/consumption contract as `resolveProjectileHitsOnBoss` — guards on `proj.active` so a shot an enemy or Gridlock pass already consumed this tick can't ALSO land here. */
export function resolveProjectileHitsOnRatKing(active: Projectile[], ratKing: RatKingBoss | null, pool: ProjectilePool): DamageEvent[] {
  if (!ratKing || ratKing.state === 'dead') return [];
  const events: DamageEvent[] = [];
  let defeated = false;
  for (const proj of active) {
    if (defeated) break;
    if (!proj.active) continue;
    if (proj.owner !== 'player') continue;
    if (!withinEngagementRange(ratKing.position, proj.position, RATKING_RADIUS + proj.radius)) continue;
    const knockDir = proj.velocity.clone().setY(0);
    const killed = ratKing.takeDamage(proj.damage, knockDir, proj.knockbackForce);
    if (killed) defeated = true;
    pool.deactivate(proj);
    events.push({ killed, scoreValue: 0, crit: proj.crit, position: ratKing.position.clone() });
  }
  return events;
}

/** Player melee (Stickball Bat) vs. the Rat King — same arc/facing gate `resolveMeleeHitsOnBoss` applies. */
export function resolveMeleeHitsOnRatKing(swing: MeleeSwing, ratKing: RatKingBoss | null, playerPos: THREE.Vector3, facing: number): DamageEvent[] {
  if (!ratKing || ratKing.state === 'dead') return [];
  if (!withinEngagementRange(ratKing.position, playerPos, swing.range + RATKING_RADIUS)) return [];
  const forward = new THREE.Vector3(Math.sin(facing), 0, Math.cos(facing));
  const toRatKing = new THREE.Vector3().subVectors(ratKing.position, playerPos).setY(0);
  if (toRatKing.lengthSq() > 1e-6 && toRatKing.angleTo(forward) > swing.arcHalfAngle) return [];
  const knockDir = toRatKing.lengthSq() > 1e-6 ? toRatKing : forward.clone();
  const killed = ratKing.takeDamage(swing.damage, knockDir, swing.knockbackForce);
  return [{ killed, scoreValue: 0, crit: swing.crit, position: ratKing.position.clone() }];
}

const SUPER_RATKING_KNOCKBACK = 4; // same reasoning as bossResolve.ts's SUPER_BOSS_KNOCKBACK — a boss-scale target shouldn't visually over-launch on a super blast tuned for light roster enemies

/** The player's super blast vs. the Rat King — mirrors `applySuperBlastOnBoss` exactly. */
export function applySuperBlastOnRatKing(playerPos: THREE.Vector3, ratKing: RatKingBoss | null, radius: number, damage: number): DamageEvent | null {
  if (!ratKing || ratKing.state === 'dead') return null;
  if (!withinEngagementRange(ratKing.position, playerPos, radius)) return null;
  const dir = new THREE.Vector3().subVectors(ratKing.position, playerPos).setY(0);
  const killed = ratKing.takeDamage(damage, dir, SUPER_RATKING_KNOCKBACK);
  return { killed, scoreValue: 0, crit: false, position: ratKing.position.clone() };
}

/**
 * The Rat King's melee (attack1) vs. the player — called when
 * `RatKingBoss.update()`'s tick result reports `meleeFired`. Unlike
 * `resolveBossShockwaveOnPlayer` (which does its own range check, since
 * Gridlock's shockwave has no notion of "in range" other than the AoE
 * radius itself), `RatKingBoss.updateAttack1` ALREADY re-checked
 * `MELEE_RANGE` against the player's live position at the exact impact
 * frame (§0.7 fire-time-capture — see ai/ratking.ts) before ever setting
 * `meleeFired` — this function applies the hit unconditionally, matching
 * `ai/enemy.ts`'s own melee split (the Enemy class decides "did this land,"
 * a separate apply step just applies it).
 */
export function resolveRatKingMeleeOnPlayer(ratKingPos: THREE.Vector3, playerPos: THREE.Vector3, playerHealth: Health, slideInvulnerable: boolean): boolean {
  if (slideInvulnerable) return false;
  const wasVulnerable = playerHealth.invulnerableT <= 0 && playerHealth.hp > 0;
  const knockDir = new THREE.Vector3().subVectors(playerPos, ratKingPos).setY(0);
  applyDamage(playerHealth, MELEE_DAMAGE, { knockbackDir: knockDir, knockbackForce: MELEE_KNOCKBACK, invulnerableAfterMs: PLAYER_HIT_INVULN_MS });
  return wasVulnerable;
}

/**
 * RED CHARGE vs. the player — called when `RatKingBoss.update()`'s tick
 * result reports `chargeHitPlayer`. Same unconditional-apply contract as
 * `resolveRatKingMeleeOnPlayer` above: `updateCharging` already did its own
 * `CHARGE_HIT_RADIUS` proximity check against the player's LIVE position
 * (not the captured target — a moving charge must be able to catch a player
 * who didn't move away in time, not just one standing exactly on the
 * frozen capture point) before setting the flag.
 */
export function resolveRatKingChargeOnPlayer(ratKingPos: THREE.Vector3, playerPos: THREE.Vector3, playerHealth: Health, slideInvulnerable: boolean): boolean {
  if (slideInvulnerable) return false;
  const wasVulnerable = playerHealth.invulnerableT <= 0 && playerHealth.hp > 0;
  const knockDir = new THREE.Vector3().subVectors(playerPos, ratKingPos).setY(0);
  applyDamage(playerHealth, CHARGE_DAMAGE, { knockbackDir: knockDir, knockbackForce: CHARGE_KNOCKBACK, invulnerableAfterMs: PLAYER_HIT_INVULN_MS });
  return wasVulnerable;
}
